import { useContext, useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import GeoContext from 'contexts/geoContext'
import {
  CHAT_CLAIM_403_MAX_RETRIES,
  CHAT_CLAIM_403_RETRY_DELAY_MS,
  isChatClaim403RetryableError,
  useGetChat,
  type GetChatResult,
} from 'tripPlanner/hooks/api/chat'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { getChat } from 'tripPlanner/api'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { combineMessages } from 'tripPlanner/utils/messageDeduplication'
import { delay } from 'tripPlanner/utils/networkError'
import type { MessagePart } from 'tripPlanner/components/Chat/Message'
import type { TripPlannerChatMessage } from 'tripPlanner/api/types'
import { subscribeAssistantStreamSyncEvents } from './assistantStreamSync'
import { hasAssistantReplyContentStarted } from './chatScrollGeometry'

type StreamingMessageMetadata = {
  oaiConversationId?: string
  rawContent?: string
  generationFinishedAt?: string
  isFinalized?: boolean
}

const mapApiMessagesToItems = (messages: Array<TripPlannerChatMessage>, oaiConversationId?: string): Array<MessageItem> => {
  return messages.map((message) => {
    const lowered = message.role.toLowerCase()
    const role: 'user' | 'assistant' = lowered === 'assistant' ? 'assistant' : 'user'

    if (role === 'assistant') {
      const parts: Array<MessagePart> = message.parts && message.parts.length > 0
        ? message.parts
        : [{ type: 'text', content: message.content }]
      const generationStatus = 'generationStatus' in message && typeof message.generationStatus === 'string'
        ? message.generationStatus
        : undefined

      return {
        id: message.id,
        role,
        parts,
        rawContent: message.rawContent || message.content,
        oaiConversationId,
        messageType: message.type,
        messageSeq: message.messageSeq,
        generationStatus,
        generationStartedAt: message.generationStartedAt,
        generationFinishedAt: message.generationFinishedAt,
        assistantTurnPolicySnapshot: message.assistantTurnPolicySnapshot,
        createdAt: message.createdAt,
        isFinalized: true,
      }
    }

    return {
      id: message.id,
      role,
      parts: [{ type: 'text', content: message.content }],
      rawContent: message.content,
      messageType: message.type,
      messageSeq: message.messageSeq,
      createdAt: message.createdAt,
      isFinalized: true,
    }
  })
}

const hasRenderableMessageContent = (message: MessageItem): boolean => {
  if (message.rawContent?.trim()) return true

  return message.parts.some((part) => {
    if (part.type !== 'text') return true
    return part.content.trim().length > 0
  })
}

const normaliseMessageContent = (message: MessageItem): string => {
  const rawContent = message.rawContent?.trim()
  if (rawContent) return rawContent

  return message.parts
    .map((part) => part.type === 'text' ? part.content.trim() : JSON.stringify(part))
    .filter(Boolean)
    .join('\n\n')
}

const matchesRenderedServerMessage = (
  overlayMessage: MessageItem,
  serverMessage: MessageItem | undefined,
): boolean => {
  if (!serverMessage || !hasRenderableMessageContent(serverMessage)) return false

  return normaliseMessageContent(serverMessage) === normaliseMessageContent(overlayMessage)
}

interface UseChatMessagesArgs {
  chatId: number | undefined
  isDevMode?: boolean
}

interface UseChatMessagesReturn {
  // Combined messages (server + overlay)
  messages: Array<MessageItem>
  filteredMessages: Array<MessageItem>

  // Streaming overlay state
  pendingUserMessage: MessageItem | undefined
  streamingMessage: MessageItem | undefined
  setPendingUserMessage: (message: MessageItem | undefined) => void
  setStreamingMessage: (message: MessageItem | undefined) => void
  updateStreamingMessageParts: (parts: Array<MessagePart>) => void
  updateStreamingMessageMetadata: (metadata: StreamingMessageMetadata) => void

  // React Query controls
  invalidateChat: (forChatId?: number) => Promise<{ skipped: boolean }>
  isQueryLoading: boolean
  isChatError: boolean
  chatError: unknown

  // Feedback map (separate from messages)
  userFeedbackMap: Record<string, 'UP' | 'DOWN'>
  setUserFeedbackMap: React.Dispatch<React.SetStateAction<Record<string, 'UP' | 'DOWN'>>>

  // Reset
  resetMessages: () => void

  // v3: true when initial query load completes (messages available for resume check)
  messagesReady: boolean
  remoteStreamStartVersion: number
}

export function useChatMessages({ chatId, isDevMode = false }: UseChatMessagesArgs): UseChatMessagesReturn {
  const queryClient = useQueryClient()
  const { currentRegionCode } = useContext(GeoContext)

  // Streaming overlay state (local only)
  const [pendingUserMessage, setPendingUserMessage] = useState<MessageItem | undefined>(undefined)
  const [streamingMessage, setStreamingMessage] = useState<MessageItem | undefined>(undefined)

  // Ref for streamingMessage so refetchOnWindowFocus can read it without re-creating the query options
  const streamingMessageRef = useRef(streamingMessage)
  streamingMessageRef.current = streamingMessage

  // React Query - source of truth for persisted messages
  // Retry on 403 to tolerate the race between POST /chats/claim (ownership
  // transfer) and this query firing with the newly-authenticated cookie.
  // C14: suppress refetchOnWindowFocus during streaming to prevent stale server data replacing overlay
  const { data: chatData, isLoading: isQueryLoading, isError: isChatError, error: chatError } = useGetChat(chatId, currentRegionCode, {
    refetchOnWindowFocus: () => streamingMessageRef.current === undefined,
    staleTime: 0,
  })
  const [userFeedbackMap, setUserFeedbackMap] = useState<Record<string, 'UP' | 'DOWN'>>({})
  const [remoteStreamStartVersion, setRemoteStreamStartVersion] = useState(0)

  // Ref to track current chatId for invalidateChat (avoids stale closure)
  const chatIdRef = useRef<number | undefined>(chatId)
  chatIdRef.current = chatId

  // Track the chatId to reset overlay on chat switch
  const previousChatIdRef = useRef<number | undefined>(chatId)

  // Clear overlay when switching between EXISTING chats (not when creating a new chat)
  // When previousChatId is undefined and chatId becomes defined, that's a new chat creation - keep overlay
  // When both are defined and different, that's switching chats - clear overlay
  // Also force a refetch to get fresh data (handles case where streaming completed while on another chat)
  useEffect(() => {
    const previousChatId = previousChatIdRef.current
    const isSwitchingBetweenExistingChats = previousChatId !== undefined && chatId !== undefined && previousChatId !== chatId

    if (isSwitchingBetweenExistingChats) {
      setPendingUserMessage(undefined)
      setStreamingMessage(undefined)
      // Force refetch to get any messages that completed while we were away
      queryClient.invalidateQueries({ queryKey: ChatKeys.detail(chatId) })
    }
    previousChatIdRef.current = chatId
  }, [chatId, queryClient])

  const serverMessages = useMemo(() => {
    if (!chatData?.messages) {
      return []
    }
    return mapApiMessagesToItems(chatData.messages, chatData.oaiConversationId)
  }, [chatData])

  // Reactive overlay cleanup: clears overlays once server data contains the
  // same rendered content. State updates are safe here because once overlays
  // are cleared, the early-return guard makes the effect a no-op.
  useEffect(() => {
    if (!pendingUserMessage && !streamingMessage) return

    const serverMessagesWithContent = new Map(
      serverMessages
        .filter(hasRenderableMessageContent)
        .map(message => [message.id, message]),
    )

    if (pendingUserMessage && matchesRenderedServerMessage(
      pendingUserMessage,
      serverMessagesWithContent.get(pendingUserMessage.id),
    )) {
      setPendingUserMessage(undefined)
    }

    if (streamingMessage && matchesRenderedServerMessage(
      streamingMessage,
      serverMessagesWithContent.get(streamingMessage.id),
    )) {
      setStreamingMessage(undefined)
    }
  }, [pendingUserMessage, streamingMessage, serverMessages])

  useEffect(() => {
    if (!chatData?.messages) return
    const feedbackMap = Object.fromEntries(
      chatData.messages
        .flatMap((m) => {
          const reaction = m.feedback?.reaction
          return reaction ? [[m.id, reaction]] : []
        }),
    )
    setUserFeedbackMap(feedbackMap)
  }, [chatData?.messages])

  // Client-generated UUIDs enable simple ID-based deduplication between local and server
  const messages = useMemo(() => {
    return combineMessages(serverMessages, pendingUserMessage, streamingMessage)
  }, [serverMessages, pendingUserMessage, streamingMessage])

  const filteredMessages = useMemo(
    () => (isDevMode
      ? messages
      : messages.filter(m =>
          m.messageType === 'message' ||
          m.messageType === 'error' ||
          m.messageType === undefined,
        )),
    [messages, isDevMode],
  )

  const updateStreamingMessageParts = useCallback((parts: Array<MessagePart>) => {
    setStreamingMessage(prev => {
      if (!prev) return undefined
      const next = { ...prev, parts }
      if (hasAssistantReplyContentStarted({ ...next, isPlaceholder: false })) {
        next.isPlaceholder = false
      }
      return next
    })
  }, [])

  const updateStreamingMessageMetadata = useCallback((metadata: StreamingMessageMetadata) => {
    setStreamingMessage(prev => {
      if (!prev) return undefined
      const next = { ...prev, ...metadata }
      // Clear placeholder once content exists, or once MESSAGE_END finalizes the turn
      // so an empty completion cannot remain a permanent zero-height ghost.
      if (
        next.isFinalized
        || hasAssistantReplyContentStarted({ ...next, isPlaceholder: false })
      ) {
        next.isPlaceholder = false
      }
      return next
    })
  }, [])

  // Invalidate React Query cache. Overlay cleanup happens in the reactive
  // effect above once matching server content is rendered.
  const invalidateChat = useCallback(async(forChatId?: number): Promise<{ skipped: boolean }> => {
    const currentChatId = chatIdRef.current

    if (forChatId !== undefined && forChatId !== currentChatId) {
      return { skipped: true }
    }

    if (!currentChatId) {
      return { skipped: true }
    }

    queryClient.invalidateQueries({ queryKey: ChatKeys.detail(currentChatId) })

    try {
      for (let attempt = 0; attempt <= CHAT_CLAIM_403_MAX_RETRIES; attempt += 1) {
        try {
          await queryClient.fetchQuery<GetChatResult>({
            queryKey: ChatKeys.detail(currentChatId),
            queryFn: () => getChat(currentChatId, currentRegionCode),
          })
          break
        } catch(error) {
          if (!isChatClaim403RetryableError(error) || attempt === CHAT_CLAIM_403_MAX_RETRIES) {
            throw error
          }

          await delay(CHAT_CLAIM_403_RETRY_DELAY_MS)
        }
      }

      return { skipped: false }
    } catch {
      // Next refetch (e.g., window focus) will sync properly.
      return { skipped: false }
    }
  }, [queryClient, currentRegionCode])

  useEffect(() => {
    if (!chatId) return undefined

    return subscribeAssistantStreamSyncEvents((event) => {
      if (event.chatId !== chatIdRef.current) return

      invalidateChat(event.chatId)
        .then((result) => {
          if (result.skipped || event.type !== 'chat_stream_started') return
          setRemoteStreamStartVersion(version => version + 1)
        })
        .catch(() => {})
    })
  }, [chatId, invalidateChat])

  const resetMessages = useCallback(() => {
    setPendingUserMessage(undefined)
    setStreamingMessage(undefined)
    setUserFeedbackMap({})
  }, [])

  return {
    messages,
    filteredMessages,
    pendingUserMessage,
    streamingMessage,
    setPendingUserMessage,
    setStreamingMessage,
    updateStreamingMessageParts,
    updateStreamingMessageMetadata,
    invalidateChat,
    isQueryLoading,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
    resetMessages,
    messagesReady: !isQueryLoading,
    remoteStreamStartVersion,
  }
}
